/* eslint-disable vue/one-component-per-file */
import EasyConfirm from "@/src/easy-ui/components/EasyConfirm.vue";
import EasyNotifications from "@/src/easy-ui/components/easyNotifications/EasyNotifications.vue";
import EasyTimePicker from "./components/easyTimePicker/EasyTimePicker.vue";
import easyNotificationsStore from "@/src/easy-ui/store/easyNotificationsStore";
import { EASY_NOTIFICATIONS_IDENTIFIER } from "@/src/easy-ui/constants/easyNotifications";
import { EasyNotificationPayload } from "@/src/easy-ui/types/easyNotifications";
import { EasyTimePickerAppPayload } from "./types/easyTimePicker";
import { LOCALE_KEYS as CONFIRM_LOCALE_KEYS } from "./constants/easyConfirm";
import { LOCALE_KEYS } from "./constants/easyTimePicker";
import { ShowEasyConfirmPayload } from "@/src/easy-ui/types/easyConfirm";
import { createApp, h } from "vue";
import { fetchLocaleStoreLocales, mountEasyUIApp } from "./utils";
import { getVuetify } from "@/src/easy-ui/plugins/vuetify";
import { EasyUIAppPayload } from "@/src/easy-ui/types";
import VueDOMPurifyHTML from "vue-dompurify-html";
import { buildAppInstance } from "@/src/utils";
import PersistentTooltip from "@/src/easy-ui/components/EasyPersistentTooltip.vue";
import { EasyPersistentTooltipPayload } from "@/src/easy-ui/types/easyPersistentTooltip";

export const createEasyTimePicker = ({ wrapperId, hiddenInputId, props }: EasyTimePickerAppPayload) => {
  const newAppPayload: EasyUIAppPayload = {
    componentPrefix: "easy-time-picker",
    hiddenInputId,
    localeKeys: LOCALE_KEYS,
    props,
    triggerUpdateEvent: (hiddenInput: HTMLInputElement) => hiddenInput.dispatchEvent(new Event("blur")),
    wrapperId,
  };

  mountEasyUIApp(EasyTimePicker, newAppPayload);
};

export const showEasyConfirm = ({
  onCancel,
  onConfirm,
  title,
  description,
  confirmBtnLabel,
  cancelBtnLabel,
  defaultSlot,
}: ShowEasyConfirmPayload) => {
  const app = createApp({
    setup() {
      fetchLocaleStoreLocales(CONFIRM_LOCALE_KEYS);

      const handleClose = () => {
        onCancel && onCancel();
      };

      const handleConfirm = () => {
        onConfirm && onConfirm();
      };

      return { handleClose, handleConfirm };
    },

    render() {
      return h(
        EasyConfirm,
        {
          title,
          description,
          confirmBtnLabel,
          cancelBtnLabel,
          "onClick:close": () => this.handleClose(),
          "onClick:ok": () => this.handleConfirm(),
        },
        {
          default: defaultSlot,
        }
      );
    },
  });

  app.use(getVuetify());
  app.mount(document.createElement("div"));
};

export const showEasyWarning = ({ title, description, confirmBtnLabel }: Partial<ShowEasyConfirmPayload>) => {
  const app = createApp({
    render() {
      return h(EasyConfirm, {
        title,
        description,
        confirmBtnLabel,
        hideCancelBtn: true,
      });
    },
  });

  app.use(getVuetify());
  app.use(VueDOMPurifyHTML);

  app.mount(document.createElement("div"));
};

export const createEasyNotifications = () => {
  const app = createApp({
    render() {
      return h(EasyNotifications);
    },
  });

  app.use(getVuetify());
  app.use(VueDOMPurifyHTML);

  return app.mount(`#${EASY_NOTIFICATIONS_IDENTIFIER}`);
};

export const showEasyNotification = (payload: EasyNotificationPayload) => {
  if (!easyNotificationsStore.easyNotificationsApp) {
    easyNotificationsStore.easyNotificationsApp = createEasyNotifications();
  }

  easyNotificationsStore.addNotification(payload);
};

export const createEasyPersistentTooltip = ({
  content,
  activatorId,
  clampedRows = 3,
}: EasyPersistentTooltipPayload) => {
  const activator = document.body.querySelector(`#${activatorId}`);
  if (!activator) return;

  const rootContainer = document.createElement("div");
  activator.appendChild(rootContainer);

  buildAppInstance({
    component: PersistentTooltip,
    rootContainer,
    props: {
      content,
      activator,
      clampedRows,
    },
  });
};
